<template>
  <div
    class="map"
    :style="fullscreenStyle"
  >
    <div
      v-if="hasWeather"
      class="weather-wrapper"
    >
      <div class="weather-desc">
        <div class="weather-desc__icon-wrapper">
          <img
            width="40"
            :src="weatherImg"
          />
        </div>
        <div class="weather-desc__content">{{ translatedWeather }}</div>
      </div>
      <p class="weather-stats">
        <span
          v-for="item in weatherStats"
          :key="item.type"
          :data-unit="item.unit"
          :data-icon-rotate="item.rotateD"
          :class="[
            'weather-stats__item',
            (item.type && item.type.toLowerCase()) || '',
            item.iconRotate && `icon-rotate-${item.iconRotate}`
          ]"
          >{{ item.text }}</span
        >
      </p>
    </div>
    <ul class="controller-wrapper">
      <li class="controller-item">
        <button
          class="scale"
          @click="enlarge"
        >
          <i
            class="iconfont enlarge-icon"
            :class="state.isFullscreen ? 'iconchumoicon7' : 'iconchumoicon6'"
          ></i>
        </button>
      </li>
      <li
        v-if="hasGisAuthority"
        class="controller-item"
      >
        <Dropdown
          class="map__layer-control"
          trigger="hover"
          @select="handlerChangeLayer"
        >
          <span class="el-dropdown-btn">
            <img
              src="~@assets/images/icon/mapLayer_icon.png"
              alt=""
              class="layer-icon"
            />
          </span>
          <template #content>
            <!-- v-for="layer in state.avaliableLayer" -->

            <div v-for="layer in state.avaliableLayer">
              <Doption
                :key="layer.key"
                :value="layer.key"
                :class="state.currentLayerType === layer.key ? 'active' : ''"
                v-if="!(useGoogleMap && layer.key === 'terrain')"
              >
                {{ layer.name }}
              </Doption>
            </div>
          </template>
        </Dropdown>
      </li>
    </ul>
    <component
      :is="whichMap"
      :selected-data="selectedData"
      :has-gis-authority="hasGisAuthority"
      :style="{
        height: state.isFullscreen ? '100vh' : null
      }"
    />
  </div>
</template>
<script setup>
import { unit } from 'mathjs';
import { getGodvMapConstructor, getGodvInstance } from './utils/get-godv';
import { GPSTransform, PERCISION } from '@client/helper/gps';
import GodV from './godV.vue';
import GoogleMap from './googleMap.vue';
import { getCurrentInstance, computed, reactive, ref, nextTick, markRaw, onMounted } from 'vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import { Dropdown, Doption } from '@arco-design/web-vue';
import { useMap } from './hooks/useMap';
import { watch } from 'vue';
import { initStore } from '@client/store/initStore';
import { useSessionStorage } from '@vueuse/core';
import { isMetricUnitType, useActivityDetail } from '../../hooks/useActivityDetail';

import * as corosSportConfig from '@coros/sport-config';

const getTemperatureUnit = corosSportConfig.basic.temperature.getUnit;
const { temperatureUnitType } = useActivityDetail();

const initStoreRef = initStore();
const sessionState = useSessionStorage('isWGS84', 0);
const THEME = initStoreRef.theme;
const isWGS84 = ref(false);

let tiandiToken = '5236e1adac113a3ac5e38527aab866d4';

const { stateMap, googleMapBounds } = useMap();

const activityDetailStoreRef = activityDetailStore();
const selectedData = computed(() => {
  return activityDetailStoreRef.selectedData;
});

const vm = getCurrentInstance();
const t = vm.proxy.$root.$t;

const whichMap = ref(null);

const state = reactive({
  whichMapName: '',
  currentLayerType: 'roadmap',
  avaliableLayer: [
    {
      name: t('C6031'),
      key: 'roadmap'
    },
    {
      name: t('C6032'),
      key: 'satellite'
    },
    {
      name: t('C6045'),
      key: 'terrain'
    }
  ],
  isFullscreen: false
});
// watch(state.currentLayerType, (val) => {
//   console.log(val);
// });
// let useGoogleMap = false;
const useGoogleMap = ref(false);
async function init() {
  const meaningfulPoint = activityDetailStoreRef.activityDetail?.frequencyList.find((item) => item.gpsLat && item.gpsLon);

  const { gpsLon, gpsLat } = meaningfulPoint;
  const isOutChina = GPSTransform.outOfChina(gpsLat / PERCISION, gpsLon / PERCISION);
  if (isOutChina) {
    // 非中国地区
    state.whichMapName = 'GoogleMap';
    useGoogleMap.value = true;
    whichMap.value = markRaw(GoogleMap);
    return;
  }
  // 判断是否包含港澳台及其他边境地区
  getGodvMapConstructor()
    .then((MapConstructor) => {
      const geocoder = new MapConstructor.Geocoder({});
      geocoder.getAddress([gpsLon / PERCISION, gpsLat / PERCISION], (_status, _result) => {
        if (_status === 'complete' && _result.info === 'OK') {
          // result为对应的地理位置详细信息
          if (_result.regeocode) {
            // const aC = _result.regeocode.addressComponent;
            const fA = _result.regeocode.formattedAddress;
            // TODO: todo_xjf aC 已经不存在 country 属性了。
            // const country = aC.country === '中国';
            const hk = fA.includes('香港');
            const mc = fA.includes('澳门');
            const tw = fA.includes('台湾');
            useGoogleMap.value = hk || mc || tw;
          } else {
            useGoogleMap.value = true;
          }
          if (useGoogleMap.value) {
            state.whichMapName = 'GoogleMap';
            whichMap.value = markRaw(GoogleMap);
          } else {
            state.whichMapName = 'GodV';
            whichMap.value = markRaw(GodV);
          }
        } else {
          state.whichMapName = 'GoogleMap';
          useGoogleMap.value = true;
          whichMap.value = markRaw(GoogleMap);
          return;
        }
      });
    })
    .catch((error) => {
      state.whichMapName = 'GoogleMap';
      useGoogleMap.value = true;
      whichMap.value = markRaw(GoogleMap);
    });
}
// 部分交互场景会暂停显示侧边栏地图，v-show 避免消耗创建地图过程
const mapCardVisible = computed(() => activityDetailStoreRef.mapCardVisible);
if (mapCardVisible.value) {
  onMounted(() => {
    nextTick(() => init());
  });
} else {
  watch(mapCardVisible, () => init());
}

const fullscreenStyle = computed(() => {
  return state.isFullscreen
    ? {
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 128,
        background: '#161b2b',
        display: 'flex'
      }
    : {};
});

const weather = computed(() => activityDetailStoreRef.weather);
const hasGisAuthority = computed(() => {
  return activityDetailStoreRef.hasGisAuthority;
});
const hasWeather = computed(() => {
  return !!weather.value;
});
const translatedWeather = computed(() => {
  if (!hasWeather.value) {
    return '';
  }

  const { provider, weatherType } = weather.value;
  const isAccu = provider === 1;
  const i18nKey = `${isAccu ? 'weather_accu_' : 'weather_seniverse_'}${weatherType}`;

  return t(i18nKey);
});

const weatherImg = computed(() => {
  if (!weather.value) {
    return '';
  }

  const { imagePath, imagePathDark, fileName } = weather.value;

  const basisPath = (THEME === 'theme-dark' && imagePathDark) || imagePath;
  return basisPath + fileName;
});

const weatherStats = computed(() => {
  if (!hasWeather.value) {
    return [];
  }

  const { temperature, humidity, windDirectionType, windSpeed } = weather.value;
  const isTemperatureMetric = temperatureUnitType.value === 0;
  const useMetric = isMetricUnitType.value;

  const celsius = temperature / 10;
  const originalSpeed = windSpeed / 10;
  const transformedSpeed = (useMetric ? originalSpeed : unit(originalSpeed, 'km').toNumber('mile')).toFixed(0);

  return [
    {
      type: 'temp',
      text: (isTemperatureMetric ? celsius : unit(celsius, 'degC').toNumber('degF')).toFixed(0) + getTemperatureUnit(isTemperatureMetric)
    },
    {
      type: 'humidity',
      text: humidity / 10,
      unit: '%'
    },
    {
      type: 'wind',
      text: `${translateWindDirection(windDirectionType)} ${transformedSpeed}`,
      iconRotate: windDirectionType * 45,
      unit: useMetric ? 'km/h' : 'mph'
    }
  ];
});

let satelliteLayer;
let terrainLayer;

function handlerChangeLayer(layerType) {
  state.currentLayerType = layerType;
  if (useGoogleMap.value) {
    // https://developers.google.com/maps/documentation/javascript/maptypes#BasicMapTypes
    stateMap.value.setMapTypeId(layerType);
  } else {
    if (layerType === 'terrain') {
      sessionState.value = 1;
      isWGS84.value = true;
      // 组件挂载
      // return;
    } else {
      sessionState.value = 0;
      isWGS84.value = false;
    }
    getGodvInstance().then(([map, MapConstructor]) => {
      map.getPitch() && map.setPitch(0);

      if (layerType === 'satellite') {
        terrainLayer?.hide();
        if (satelliteLayer) {
          satelliteLayer.show();
          return;
        }
        satelliteLayer = new MapConstructor.TileLayer.Satellite();
        map.add([satelliteLayer]);
      } else if (layerType === 'terrain') {
        satelliteLayer?.hide();
        if (terrainLayer) {
          terrainLayer.show();
          map.setPitch(45);
          return;
        }
        terrainLayer = new MapConstructor.TileLayer({
          tileUrl: `https://t0.tianditu.gov.cn/img_w/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX=[z]&TILEROW=[y]&TILECOL=[x]&tk=${tiandiToken}`,
          opacity: 1,
          zooms: [0, 20],
          zIndex: 30
        });
        map.add([terrainLayer]);
        map.setPitch(45);
      } else {
        terrainLayer?.hide();
        satelliteLayer?.hide();
      }
    });
  }
}
function enlarge() {
  state.isFullscreen = !state.isFullscreen;
  // netxTick 后地图大小尚未改变，所以需要延迟一下
  setTimeout(() => {
    if (state.whichMapName === 'GoogleMap') {
      const center = googleMapBounds.value.getCenter();
      console.log(`🔔 center`, center);
      stateMap.value.setCenter(center);
      stateMap.value.fitBounds(googleMapBounds.value);
    } else {
      stateMap.value.setFitView(stateMap.value.polyline);
    }
  }, 100);
}
function translateWindDirection(val) {
  const i18nKeys = {
    0: 'wind_north',
    1: 'wind_northeast',
    2: 'wind_east',
    3: 'wind_southeast',
    4: 'wind_south',
    5: 'wind_southwest',
    6: 'wind_west',
    7: 'wind_northwest'
  };

  return i18nKeys[val] && t(i18nKeys[val]);
}
</script>

<style lang="less" scoped>
.animate-opacity-mixin() {
  opacity: 0;
  transition: opacity 0.5s;
  transition-delay: 0.2s;
}

.stats-icon-mixin() {
  display: inline-block;
  margin-right: 0.25em;
  width: 16px;
  height: 16px;
  background-size: cover;
  content: '';
  vertical-align: text-bottom;
}

.map {
  position: relative;
  width: 100%;
  height: 100%;

  .weather-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 5;
    overflow: hidden;
    padding: 0 12px 8px 5px;
    width: 80px;
    max-height: 68px;
    color: #fff;
    background: rgb(60 64 79 / 60%);
    box-shadow: 0 8px 32px 0 rgb(31 38 135 / 37%);
    transition: width 0.5s;
    backdrop-filter: blur(20px);
    border-top-right-radius: 100px;
    border-bottom-right-radius: 100px;
    box-sizing: border-box;

    .weather-desc {
      display: flex;
      align-items: center;
      max-height: 40px;
    }

    .weather-desc__icon-wrapper {
      width: 50px;
      font-size: 0;
    }

    .weather-desc__content {
      flex-grow: 1;
      width: 1px;
      word-break: keep-all;
      font-size: 12px;
      .animate-opacity-mixin();
    }

    .weather-stats {
      overflow: hidden;
      max-height: 20px;
      font-size: 18px;
      white-space: nowrap;
      line-height: 1.15;
    }

    .weather-stats__item {
      margin-left: 10px;

      &.temp {
        display: inline-block;
        margin-left: 0;
        width: 45px;
        text-align: center;

        // &::after {
        //    content: '\00B0';
        // }
      }

      &.humidity {
        margin-left: 7px;
        font-size: 14px;
        .animate-opacity-mixin();

        &::before {
          background: url('@assets/images/icon/humidity.png');
          .stats-icon-mixin();
        }

        &::after {
          content: attr(data-unit);
        }
      }

      &.wind {
        font-size: 12px;
        .animate-opacity-mixin();

        &::before {
          text-align: bottom;
          background: url('@assets/images/icon/windDirection.png');
          .stats-icon-mixin();
        }

        each(range(7), {
          @rotateDeg: @value * 45;
          &.icon-rotate-@{rotateDeg}::before {
            transform: rotate(@rotateDeg * 1deg);
          }
        })
          &::after {
          content: attr(data-unit);
          font-size: 0.7em;
        }
      }
    }

    &:hover {
      width: 100%;
      border-radius: 0;

      .weather-desc__content {
        word-break: break-all;
        opacity: 1;
      }

      .humidity,
      .wind {
        opacity: 1;
      }
    }
  }

  .controller-wrapper {
    position: absolute;
    right: 12px;
    bottom: 0;
    z-index: 10;
    padding-bottom: 16px;

    .controller-item {
      margin-top: 12px;

      &:first-child {
        margin-top: 0;
      }
    }
  }

  .map__layer-control {
    color: #fff;
  }

  .scale {
    z-index: 1;
    display: inline-block;
    padding: 8px;
    font-size: 0;
    background: rgb(60 64 79 / 60%);
    border-radius: 50%;
    box-shadow: 0 8px 32px 0 rgb(31 38 135 / 37%);
    backdrop-filter: blur(20px);
    cursor: pointer;
    line-height: 1;

    .enlarge-icon {
      font-size: 16px;
      color: rgb(255 255 255 / 60%);
    }
  }

  :deep(.lap-marker) {
    padding: 2px 4px;
    font-size: 12px !important;
    text-align: center;
    white-space: nowrap;
    color: white !important;
    background: #0e152a;
    border: 1px solid #fff;
    border-radius: 7px;
    line-height: 1;
    word-break: normal;

    &.dark {
      border-color: #e3e3e3;
    }
  }

  :deep(.amap-marker-label) {
    background: transparent;
    border: none;
  }
}
</style>

<style lang="scss">
.map__layer-control {
  background: #434b60;
  padding: 0;
  margin: 0;
  .arco-dropdown {
    padding: 0;
  }
  .arco-dropdown-option {
    background: #434b60 !important;
    line-height: 2;
    padding: 0 34px;

    &:hover,
    &.active {
      background: #575d71 !important;
      color: #00b3ff !important;
    }
  }
}

.el-dropdown-btn {
  background: rgba(60, 64, 79, 0.6);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(20px);
  padding: 8px;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  font-size: 0;
}

.layer-icon {
  display: inline-block;
  width: 16px;
}
</style>
